import { useQuery } from '@tanstack/react-query';
import { fetchCombined, toDayParam, type CombinedQuery } from '../api/businessAccessCombined';
import { fetchPassStats } from '../api/activityStats';
import type { BusinessAccess } from './useBusinessAccessInvitations';
import { useEvents } from './useEvents';
import { useUsers } from './useUsers';
import { PENDING_USER_STATUSES, PENDING_USER_TYPES } from '../pages/Users/pendingStatuses';
import {
  ARRIVING_STATUSES, arrivingNext, nextEvents, oldestAgeDays, passCounts, periodWindow,
  type ArrivalItem, type DashboardPeriod, type NextEventItem, type PassCounts,
} from '../utils/tenantDashboard';

// SAM-942 · D2 Tenant dashboard. Existing endpoints only: /business_access/stats (counts by status, with
// estate_group_id since lambdas #841), /business_access/combined (the rows of Arriving next), /user (the
// Pending list) and /event.

const ARRIVING_LOOKAHEAD_DAYS = 7;
const ARRIVING_FETCH_LIMIT = 200;
/** «Inside now» looks this many days back: a stay that began earlier and has not checked out yet. */
const INSIDE_LOOKBACK_DAYS = 7;
const PENDING_FETCH_LIMIT = 100;

export interface DashboardScope {
  /** Residential scope for property managers, like the Overview dashboard. */
  estateId?: string;
  /** The Estate group filter of the header. */
  estateGroupId?: string;
}

const day = (offset: number) => {
  const now = new Date();
  return toDayParam(new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset));
};

/** Expected, Valid and Entered of the period: one stats request, a pass counts on the day it starts. */
export const useTenantPassCounts = (period: DashboardPeriod, scope: DashboardScope = {}) => {
  const w = periodWindow(period);
  return useQuery<PassCounts>({
    queryKey: ['tenant-dashboard', 'counts', w.dateFrom, w.dateTo, scope.estateGroupId ?? null],
    queryFn: async () => {
      const stats = await fetchPassStats({ date_from: w.dateFrom, date_to: w.dateTo, estate_group_id: scope.estateGroupId });
      return passCounts(stats.by_status);
    },
    staleTime: 30 * 1000,
  });
};

/** Checked in, not out yet, among the passes that started in the last days. */
export const useInsideNow = (scope: DashboardScope = {}) => {
  const to = day(0);
  return useQuery<number>({
    queryKey: ['tenant-dashboard', 'inside-now', to, scope.estateGroupId ?? null],
    queryFn: async () => {
      const stats = await fetchPassStats({ date_from: day(-INSIDE_LOOKBACK_DAYS), date_to: to, estate_group_id: scope.estateGroupId });
      return passCounts(stats.by_status).inside;
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
};

/** Today (from now on) and the next days. /combined has no estate group filter: the group narrows the rows here. */
export const useArrivingNext = (scope: DashboardScope = {}, limit = 5) => {
  const from = day(0);
  const to = day(ARRIVING_LOOKAHEAD_DAYS);
  return useQuery<ArrivalItem[]>({
    queryKey: ['tenant-dashboard', 'arriving-next', from, scope.estateId ?? null, scope.estateGroupId ?? null, limit],
    queryFn: async () => {
      const base: CombinedQuery = { ...(scope.estateId ? { estate_id: scope.estateId } : {}), date_from: from, date_to: to, page: 1, limit: ARRIVING_FETCH_LIMIT };
      // /combined has no status IN filter: one request per status that has not arrived yet.
      const pages = await Promise.all(
        ARRIVING_STATUSES.map((s) => fetchCombined<BusinessAccess>({ ...base, business_access_status_id: s })),
      );
      return arrivingNext(pages.flatMap((p) => p?.data || []), new Date(), limit, scope.estateGroupId);
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
};

/** Users waiting in Users > Pending: the count is that tab's total; the age comes from the first rows. */
export const usePendingRequests = () => {
  const { users, totalUsers, isLoading, error } = useUsers({
    page: 1,
    limit: PENDING_FETCH_LIMIT,
    owner_resident_status: [...PENDING_USER_STATUSES],
    user_type: [...PENDING_USER_TYPES],
  });
  const count = totalUsers as number;
  // Only trustworthy when every pending user came back.
  const oldestDays = count > 0 && users.length >= count
    ? oldestAgeDays(users.map((u) => (u as { create_date?: string }).create_date))
    : undefined;
  return { count, oldestDays, isLoading, error };
};

export const useNextEvents = (limit = 5) => {
  const { data, isLoading, error } = useEvents();
  const items: NextEventItem[] = data ? nextEvents(data, new Date(), limit) : [];
  return { items, isLoading, error };
};
